// 전체코드
// src/pages/course/ListCoursePage.js

import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import ListAddButton from "../../components/common/ListAddButton";
import CourseSearchBar from "../../components/course/CourseSearchBar";
import CourseFilterBar from "../../components/course/CourseFilterBar";
import CourseListItem from "../../components/course/CourseListItem";

import {
  getCourseList,
  listCoursePathPoints,
  getAddingWorkRecord,
  giveupCourse,
} from "../../api/courseApi";
import { buildGoogleStaticMapUrl } from "../../utils/googleStaticMap";
import { useJWT } from "../../contexts/JWTContext";
import { useToast } from "../../contexts/ToastContext";

const DRAFT_KEY_PREFIX = "wingstep_course_adding_draft";

function getDraftKey(userId) {
  return `${DRAFT_KEY_PREFIX}:${userId || "unknown"}`;
}

function readDraft(userId) {
  try {
    const raw = localStorage.getItem(getDraftKey(userId));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function clearDraft(userId) {
  try {
    localStorage.removeItem(getDraftKey(userId));
  } catch (e) {}
}

function isValidDraft(draft, expectedWorkRecordId) {
  if (!draft) return false;

  const type = String(draft.type || "").trim();
  const courseName = String(draft.courseName || "").trim();

  if (!type) return false;
  if (!courseName) return false;

  // draft에 workRecordId를 저장하고 있다면 서버 workRecordId와 일치해야 한다
  if (
    draft.workRecordId !== undefined &&
    draft.workRecordId !== null &&
    draft.workRecordId !== "" &&
    Number(draft.workRecordId) !== Number(expectedWorkRecordId)
  ) {
    return false;
  }

  return true;
}

function ListCoursePage() {
  const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_STATIC_MAP_KEY;

  const navigate = useNavigate();
  const { userId } = useJWT();
  const { showToast } = useToast();

  const [search, setSearch] = useState("");
  const [minDistance, setMinDistance] = useState("");
  const [maxDistance, setMaxDistance] = useState("");
  const [onlyMyCourse, setOnlyMyCourse] = useState(false);

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handlingAddRef = useRef(false);

  async function loadCourseList() {
    try {
      setLoading(true);
      setError(null);

      const data = await getCourseList({
        keyword: search || undefined,
        minDistance: minDistance || undefined,
        maxDistance: maxDistance || undefined,
        onlyMyCourse,
        userId: userId,
      });

      const mappedBase = (data || []).map((course) => ({
        raw: course,
        courseId: course.courseId,
        name: course.courseName,
        distance: course.distance,
        startLocation: course.startLocation || "출발지 정보 없음",
        type: course.type,
        imageUrl: null,
        ratingAvg: course.avgRating ? Number(course.avgRating) : null,
      }));

      setCourses(mappedBase); // 일단 목록을 먼저 보여줌
      setLoading(false);

      // 2. 그 후, 비동기적으로 지도를 하나씩 업데이트합니다.
      mappedBase.forEach(async (course, index) => {
        if (!GOOGLE_KEY) return;
        try {
          const points = await listCoursePathPoints(course.courseId);
          if (points && points.length > 0) {
            const thumbUrl = buildGoogleStaticMapUrl(points, {
              apiKey: GOOGLE_KEY,
              size: "200x200",
              zoom: 13,
            });

            // 해당 항목만 이미지를 업데이트
            setCourses((prev) =>
              prev.map((c) =>
                c.courseId === course.courseId
                  ? { ...c, imageUrl: thumbUrl }
                  : c
              )
            );
          }
        } catch (e) {
          console.error(`지도 로드 실패 (${course.courseId}):`, e);
        }
      });
    } catch (e) {
      console.error(e);
      setError(e);
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCourseList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDistance, maxDistance, onlyMyCourse]);

  const handleClickAdd = async () => {
    if (handlingAddRef.current) return;
    handlingAddRef.current = true;

    try {
      if (!userId) {
        showToast("로그인 정보가 없습니다.", "e");
        return;
      }

      // 1) 서버에 생성중(측정중 + courseId null) workRecord가 있는지 확인
      let addingWorkRecordId = null;
      try {
        const resp = await getAddingWorkRecord(userId);
        addingWorkRecordId = resp?.workRecordId ?? null;
      } catch (e) {
        console.error("생성중 운동기록 조회 실패:", e);
      }

      // 생성중 없음 -> 기존 흐름
      if (!addingWorkRecordId) {
        navigate("/course/add");
        return;
      }

      // 2) 생성중 있음 -> localStorage draft 검사
      const draft = readDraft(userId);

      // localStorage 소실/불일치 -> 토스트 + 강제종료 + 생성화면 이동
      if (!isValidDraft(draft, addingWorkRecordId)) {
        showToast("데이터가 소실되어 복구가 불가능합니다", "w");

        try {
          await giveupCourse(addingWorkRecordId);
        } catch (e) {
          console.error("생성중 강제종료 실패:", e);
        } finally {
          clearDraft(userId);
        }

        navigate("/course/add", { replace: true });
        return;
      }

      // 3) draft 정상 -> 이어하기 확인
      const ok = window.confirm(
        "기존에 생성중인 코스가 있습니다. 이어하시겠습니까?"
      );
      if (ok) {
        navigate("/course/adding", {
          state: {
            workRecordId: addingWorkRecordId,
            type: draft.type,
            courseName: draft.courseName,
          },
        });
        return;
      }

      // 4) 아니오 -> 강제종료 + 생성화면 이동
      try {
        await giveupCourse(addingWorkRecordId);
      } catch (e) {
        console.error("생성중 포기 실패:", e);
      } finally {
        clearDraft(userId);
      }

      navigate("/course/add");
    } finally {
      handlingAddRef.current = false;
    }
  };

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex flex-col max-w-md mx-auto relative">
      <header className="px-4 pt-6 pb-4 bg-white sticky top-0 z-10 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)]">
        <h1 className="text-2xl font-bold text-gray-800 text-center mb-5">
          코스 찾기
        </h1>

        <div className="flex flex-col gap-1">
          <CourseSearchBar
            value={search}
            onChange={setSearch}
            onSearch={loadCourseList}
          />
          <CourseFilterBar
            minDistance={minDistance}
            maxDistance={maxDistance}
            onMinChange={setMinDistance}
            onMaxChange={setMaxDistance}
            onlyMyCourse={onlyMyCourse}
            onToggleOnlyMyCourse={() => setOnlyMyCourse((prev) => !prev)}
          />
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-5 py-6 pb-24 scrollbar-hide">
        {loading && (
          <div className="text-center text-gray-500 py-10">로딩 중...</div>
        )}
        {error && (
          <div className="text-center text-red-500 py-10">에러 발생</div>
        )}

        {!loading && !error && courses.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <p>조건에 맞는 코스가 없습니다.</p>
          </div>
        )}

        <div className="space-y-6">
          {courses.map((course) => (
            <CourseListItem
              key={course.courseId}
              imageUrl={course.imageUrl}
              name={course.name}
              distance={course.distance}
              startLocation={course.startLocation}
              type={course.type}
              ratingAvg={course.ratingAvg}
              onClick={() => navigate(`/course/${course.courseId}`)}
            />
          ))}
        </div>
      </main>

      <div className="absolute bottom-24 right-5 z-20">
        <ListAddButton onClick={handleClickAdd} />
      </div>
    </div>
  );
}

export default ListCoursePage;
